import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Activity } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

const TONE = {
  ok: { dot: "bg-emerald-500", label: "Working", text: "text-emerald-800" },
  warn: { dot: "bg-amber-500", label: "Needs attention", text: "text-amber-800" },
  error: { dot: "bg-rose-500", label: "Not working", text: "text-rose-700" },
  off: { dot: "bg-stone-300", label: "Not set up", text: "text-brand-tertiary" },
};

function ServiceCard({ s }) {
  const t = TONE[s.status] || TONE.off;
  return (
    <div className="border border-brand-border bg-brand-page rounded-lg p-4 space-y-1.5" data-testid={`health-${s.key}`}>
      <div className="flex items-center gap-2">
        <span className={`h-2.5 w-2.5 rounded-full ${t.dot} ${s.status === "error" ? "animate-pulse" : ""}`} />
        <p className="text-sm font-medium text-brand-ink flex-1">{s.name}</p>
        <span className={`text-[10px] uppercase tracking-[0.12em] ${t.text}`} data-testid={`health-${s.key}-status`}>{t.label}</span>
      </div>
      <p className="text-[11px] text-brand-tertiary">{s.purpose}</p>
      <p className="text-xs text-brand-muted leading-relaxed" data-testid={`health-${s.key}-detail`}>{s.detail}</p>
    </div>
  );
}

export function IntegrationHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData((await api.get("/admin/integrations/health")).data); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't run the checks — refresh to retry."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const issues = (data?.services || []).filter((s) => s.status === "error" || s.status === "warn").length;
  return (
    <div className="bg-brand-surface border border-brand-border p-6 space-y-5" data-testid="integration-health">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[240px]">
          <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> Integration health</p>
          <p className="text-[11px] text-brand-muted mt-1" data-testid="health-summary">
            {loading ? "Checking every connected service live…" : data ? `${issues ? `${issues} need${issues === 1 ? "s" : ""} attention` : "Everything connected is working"} · checked ${new Date(data.checked_at).toLocaleTimeString()}` : ""}
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="health-recheck"
          className="inline-flex items-center gap-1.5 border border-brand-border rounded-full px-4 py-1.5 text-sm text-brand-ink hover:bg-brand-hover transition-colors disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Re-check
        </button>
      </div>
      {error && <p className="text-sm text-brand-neg" data-testid="health-error">{error}</p>}
      {loading && !data && <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-24 rounded-lg bg-brand-hover animate-pulse" />)}</div>}
      {data && <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">{data.services.map((s) => <ServiceCard key={s.key} s={s} />)}</div>}
    </div>
  );
}
